import { Link, useOutletContext } from 'react-router-dom';
import { EmptyState } from '@/components/feedback/EmptyState';
import { Button } from '@/components/foundation/Button';
import { PageTemplate } from '@/components/page/PageTemplate';
import type { Portal } from '@/types/api';
import type { BreadcrumbSegment } from '@/types/nav';

interface ShellOutletContext {
  breadcrumb?: BreadcrumbSegment[];
}

interface NotFoundPageProps {
  /** When set, renders inside Admin/Tenant shell with portal home link. */
  portal?: Portal;
}

export default function NotFoundPage({ portal }: NotFoundPageProps) {
  const context = useOutletContext<ShellOutletContext>();
  const breadcrumb = context?.breadcrumb ?? [];
  const portalHome = portal === 'admin' ? '/admin/home' : portal === 'tenant' ? '/tenant/home' : null;

  const content = (
    <>
      <EmptyState
        title="Page not found"
        description="The page you requested does not exist or has moved."
      />
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        {portalHome ? (
          <Link to={portalHome}>
            <Button variant="ghost">Go to home</Button>
          </Link>
        ) : (
          <>
            <Link to="/public/login">
              <Button variant="ghost">Go to login</Button>
            </Link>
            <Link to="/admin/home">
              <Button variant="ghost">Admin home</Button>
            </Link>
          </>
        )}
      </div>
    </>
  );

  if (portal) {
    return (
      <PageTemplate title="Page not found" breadcrumb={breadcrumb}>
        <div className="flex flex-col items-center px-6 py-8">{content}</div>
      </PageTemplate>
    );
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center px-6">{content}</div>
  );
}
